'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import { client, LEAD_TIME_SIGNAL_DAYS, LEAD_TIMES } from '../demo-data'
import { activityLabel, leadTimeStatus, useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, AppSubHeader, HeaderStat } from './app-screen'
import styles from './lead-time-visual.module.css'
import { useScenario } from './use-scenario'

// The signal list is the shared lead-time list itself — the same rows the dashboard tile counts.
const ROWS = LEAD_TIMES

const columns = (t: VisualCopy) => [
  t.word.client.toUpperCase(),
  t.screen.leadTime.title.toUpperCase(),
  t.word.status.toUpperCase(),
]

/** Resolved in CSS, so a container query can restate it for a narrow card (see the module). */
const GRID = 'var(--lt-cols)'

/**
 * Day counts per beat for the rows whose lead time runs on; every other row holds its listed value.
 *
 * Three dossiers move rather than one, and two of them go over the threshold. A single climber
 * between four frozen rows made the list look like a still with one animated number in it, and it
 * put exactly one dossier in trouble — which is not what a signal list is for. The third climbs
 * hard and stops short of the line, so crossing it reads as something a dossier can avoid.
 *
 * They move in turn, not together: the first crosses and takes the top, then the second crosses,
 * then the third climbs underneath. Each arrival gets its own moment, and the threshold line drops
 * a row at a time instead of jumping two.
 *
 * The last value of each is that row's listed value, so the resting frame IS the shared data — the
 * same numbers the dashboard tile and the assistant read.
 */
const CLIMBS: Readonly<Record<number, readonly number[]>> = {
  0: [82, 91, 101, 108, 112, 112, 112, 112, 112],
  1: [87, 87, 87, 87, 87, 89, 94, 94, 94],
  2: [64, 64, 64, 64, 64, 64, 64, 71, 78],
}

/**
 * Hold times per beat. The two threshold crossings are the beats worth watching, and they are held
 * roughly twice as long as the counting beats around them.
 */
const BEAT_HOLDS = [1100, 1500, 800, 800, 1400, 900, 1600, 900, 2200, 700] as const
const REST_BEAT = 8 // SSR / reduced-motion / not-yet-playing snapshot — two flagged, list settled
// The reset demotes the flagged rows and jump-cuts their counts back down, so the loop must not
// enter there: a hover start would make that un-doing the first thing a visitor sees.
const RESET_BEAT = 9 // final loop beat: already reads as the pre-climb starting state

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive row state;
 *  the final loop beat already reads as the pre-climb start, for a seamless wrap. */
function effectiveBeat(beat: number | null): number {
  if (beat === null) return REST_BEAT
  return beat >= RESET_BEAT ? 0 : beat
}

/** What row `index` shows at `beat`: its climb, if it has one, otherwise its listed value. */
function dayCount(index: number, beat: number): number {
  const climb = CLIMBS[index]
  if (!climb) return ROWS[index].days
  return climb[Math.min(beat, climb.length - 1)]
}

/**
 * Grip card 3 — "Doorlooptijd / Langst open" signal list: the flagged item's day count climbs
 * (82→112), crosses the 90-day signal threshold and turns amber, then animates to the top of the
 * list. Driven by `useScenario`'s 8-beat loop; `beat === null` (SSR, no-JS,
 * `prefers-reduced-motion: reduce`) renders the resolved rest state: flagged, at the top.
 */
export function LeadTimeVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)

  // Derived, not scheduled: the list ranks itself off the counts it is currently showing, and a row
  // flags the moment it crosses the threshold. Both used to be fixed beats, so the list sat visibly
  // mis-sorted for seconds while showing 101 days against a 90-day line. With three counters moving
  // this is also the only sane way to order them.
  const counts = ROWS.map((row, index) => ({ index, days: dayCount(index, effBeat) }))
  // Which counters moved on this beat. A day count changing is the whole event on this card, and at
  // 11px it was two digits quietly swapping while the eye was somewhere else.
  const previous = ROWS.map((row, index) => dayCount(index, Math.max(effBeat - 1, 0)))
  const ranked = [...counts].sort((a, b) => b.days - a.days || a.index - b.index)
  const rankOf = new Map(ranked.map((entry, rank) => [entry.index, rank]))
  const flaggedCount = counts.filter((entry) => entry.days > LEAD_TIME_SIGNAL_DAYS).length

  return (
    <AppUiVisual label={t.screen.leadTime.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="lead-time" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.leadTime.title}
            detail={t.screen.leadTime.longestOpen}
            right={<HeaderStat value={`${LEAD_TIME_SIGNAL_DAYS} ${t.word.days}`} label={t.screen.leadTime.threshold} />}
          />

          <div className={clsx(styles.body, 'flex min-h-0 flex-1 flex-col')}>
            <AppSubHeader
              rowLabel={columns(t)[0]}
              columns={[
                { label: columns(t)[1] },
                { label: columns(t)[2], align: 'right' as const, className: styles.statusCell },
              ]}
              style={{ gridTemplateColumns: GRID }}
            />

            {/* Rows are absolutely positioned at a share of the track rather than at a fixed pixel
                height, so the list fills whatever height the card has — and the climbing row can
                still translate a whole slot upward, since one slot is exactly its own height. */}
            <div className="min-h-0 flex-1 px-3 py-1">
              <div className={styles.rowTrack}>
                {/* The 90-day line, drawn where it actually falls in the list. The threshold was
                    stated only in the header, so a climb from 82 to 112 days was a number changing
                    rather than a line being crossed — and crossing it is the whole point of a
                    signal. It sits above everything while every dossier is inside the threshold,
                    and drops one row per crossing — so a row crosses the line rather than the line
                    following the row, and how many are in trouble is legible as a position. */}
                <div
                  className={styles.threshold}
                  style={{ top: `${flaggedCount * (100 / ROWS.length)}%` }}
                  aria-hidden="true"
                >
                  <span className={styles.thresholdLabel}>
                    {LEAD_TIME_SIGNAL_DAYS} {t.word.days}
                  </span>
                </div>
                {ROWS.map((row, index) => {
                  const rowDays = dayCount(index, effBeat)
                  const rowFlagged = rowDays > LEAD_TIME_SIGNAL_DAYS
                  const rank = rankOf.get(index) ?? index
                  const justChanged = effBeat > 0 && rowDays !== previous[index]
                  return (
                    <div
                      key={row.nr}
                      // The sub-header's own gap, so both grids resolve their `fr` columns from the
                      // same free space and the headings land exactly over their columns.
                      className={clsx(styles.row, 'grid items-center gap-2')}
                      style={{
                        gridTemplateColumns: GRID,
                        height: `${100 / ROWS.length}%`,
                        transform: `translateY(${rank * 100}%)`,
                      }}
                    >
                      <span className="flex min-w-0 flex-col gap-px">
                        <span className="truncate text-[11px] font-semibold" style={{ color: 'var(--au-accent)' }}>
                          {client(row.nr).short}
                        </span>
                        <span className="truncate text-[9.5px]" style={{ color: 'var(--au-text2)' }}>
                          {activityLabel(t, row.activity) ?? row.short} ({row.year})
                        </span>
                      </span>
                      {/* Re-keyed on the value, so the flash restarts every time the number changes
                          rather than only when the element first appears. */}
                      <span
                        key={rowDays}
                        className={clsx(
                          styles.days,
                          justChanged && (rowFlagged ? styles.daysRoseOver : styles.daysRose),
                          'text-center text-[11px] font-bold whitespace-nowrap tabular-nums',
                        )}
                        style={{ color: rowFlagged ? 'var(--au-sig)' : 'var(--au-text)' }}
                      >
                        {rowDays} {t.word.days}
                      </span>
                      <div className={clsx(styles.statusCell, styles.statusBody)}>
                        <span
                          className={clsx(styles.pill, 'truncate')}
                          style={{ background: rowFlagged ? 'var(--au-sig)' : 'var(--au-busy)' }}
                        >
                          {leadTimeStatus(t, row.status)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
